import { useEffect, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { StockRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import ESpinner from 'components/others/Spinner';
import { ProductTitle, useProduct } from 'modules/product';
import StockLotsTable, { type StockLotRow } from './StockLotsTable';

interface OffStockLotsProps {
    stock: StockRow | null;
    onHide: () => void;
}

const toLot = (row: Record<string, unknown>): StockLotRow => ({
    productId: Number(row?.productId || 0),
    operPrice: row?.operPrice == null ? null : Number(row.operPrice),
    operPriceRate: row?.operPriceRate == null ? null : Number(row.operPriceRate),
    stock: Number(row?.stock || 0),
});

const OffStockLots = ({ stock, onHide }: OffStockLotsProps) => {
    const { t } = useTranslation(['Warehouses']);
    const { openProductCard } = useProduct();
    const [lots, setLots] = useState<StockLotRow[] | null>(null);

    useEffect(() => {
        const warehouseId = Number(stock?.warehouseId || 0);
        const productId = Number(stock?.productId || 0);
        if (!warehouseId || !productId) {
            setLots(null);
            return;
        }

        let cancelled = false;
        setLots(null);

        const load = async () => {
            const res = await apiWarehouse.getStockByProductId(warehouseId, productId);
            if (cancelled) return;
            const rows = Array.isArray(res) ? res.map((row) => toLot(row as Record<string, unknown>)) : [];
            setLots(rows);
        };

        void load();
        return () => {
            cancelled = true;
        };
    }, [stock?.warehouseId, stock?.productId]);

    return (
        <Offcanvas
            show={stock !== null}
            onHide={onHide}
            placement="end"
            className="off-import off-stock-lots"
        >
            <Offcanvas.Header closeButton className="border-bottom">
                <Offcanvas.Title>{t('toolStockLots')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="px-3 pb-3">
                    <ProductTitle
                        product={stock}
                        showArticle
                        onPhotoClick={() => {
                            const productId = Number(stock?.productId || 0);
                            if (productId > 0) openProductCard(productId, 'photo');
                        }}
                    />
                </div>
                <div className="px-3 pb-3">
                    {lots ? <StockLotsTable rows={lots} /> : <ESpinner />}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffStockLots;
